import React from "react";
import { Hero, PageContent } from "../../components";

function Implementation() {
  return (
    <div className="flex overflow-x-hidden overflow-y-hidden relative flex-col z-8">
      <Hero title={"Implementation"}>
        Ideas find their {" "}
        <span className="text-lg font-black text-RougePastel md:text-xl lg:text-2xl">
          true value
        </span>{" "}
        when they become reality.
      </Hero>

      <PageContent>
        {
          // @ts-ignore
          `
## Context
Our project, "Clean Heat," offers an innovative solution for sustainable heating and water purification in residential settings. We propose the integration of our biotechnology-based system into households, providing a host of benefits that address both energy and environmental concerns. 

After discussing with the leader of wastewater treatment plant in France (Veolia), we have learned that it was difficult to change the process of a treatment plant. The easier way to tackle water depollution then is to implement it at the exit of the source of this pollution: households. Indeed, EE2 are released after consumption of contraceptive pills by women and are thrown in the toilets. Moreover, implementing our system close to residential areas would enable to decrease the heat loss from our system. 

## Design Thinking

To better understand the implementation of our system, we have conceived a 3D Model of our project with SketchUp software. It aims at providing a comprehensive visual representation of our entire system. 

<div class="image_frame">
  <img class="illustration medium" id="img1" src="https://static.igem.wiki/teams/4577/wiki/project/implementation/implementation.jpg">
</div>

### Underground and Isolated Design

   - Clean Heat's underground ensures that it doesn't occupy valuable above-ground space. Moreover, it enables to isolate the photobioreactor to limit the heat loss and maintain a constant temperature regardless of the weather. This design consideration is ideal for properties with limited surface area or aesthetic concerns. 



### Photobioreactor Model

Our photobioreactor is designed to provide an ideal habitat for our microorganisms while simultaneously maintaining precise control over their well-being. To achieve this, we incorporated several key features into our 3D model: 

- **LED Light Slits:** We integrated built-in slits for LED lights, ensuring that our microorganisms receive the optimal amount of light for proper growth and photosynthesis. 	The optimum light intensity is 26−400 µmol photons m−2 s−1 [1].  

 

- **Sensor-Enabled Window:** A window equipped with sensors is included to monitor and 	regulate essential environmental factors such as oxygen (O2) levels, carbon dioxide (CO2) 	levels, temperature and pH within the habitat. 

 

### Adjacent Components

In addition to the photobioreactor itself, our 3D model represents several additional components that are necessary to our system's functionality: 

- **Wastewater Tank:** Positioned on the left side of our photobioreactor, we have installed 	a tank to collect and store excess wastewater. This tank serves as a reservoir to fill the photobioreactor when needed, that is why it features a level sensor valve for automated control. 

- **Water-to-Water Heat Pump and Conduction Heat Exchanger:** On the right side of our 	photobioreactor, we have incorporated a water-to-water heat pump coupled with a 		double-pipe acting as a heat exchanger by conduction within the habitat. This system produces hot water at a temperature of 60°C, which can be used home. 

 

### Biomass Harvesting and Filtration System

In addition to the core components of our photobioreactor, we have implemented a biomass harvesting and filtration system to optimize the collection of valuable biomass while maintaining the overall health of our microorganism culture. 

At the base of the photobioreactor, we have incorporated a small drainage valve designed to facilitate the removal of decanted dead cells. This placement allows the easy extraction of the biomass and keeps the culture environment clean for the growth of healthy microorganisms. 

Following the drainage valve, we have integrated a filtration system. This system is designed to collect the biomass, and if some water goes through with it, it will be directed towards a wastewater treatment station. The filtration process effectively separates the valuable biomass from unwanted impurities. 

## Regulation 

The implementation and commercialization of GMO-based products differs according to their use: products intended for consumption are not subject to the same regulation as non-consumable elements. The latter are governed by the directive 2001/18/CE at the European scale applying to France. The Clean Heat system requires the respect of several European regulations. Indeed, GMOs can’t be released on the market without a previous authorization with a 10-year renewable validity delivered by the EFSA (European Food Safety Authority).  In addition, the labelling of GMOs and products derived from GMOs is mandatory, and European regulations provide that any GMO authorized for marketing in the European Union must be accompanied by a detection method [2].   

Containment is an essential element of risk management for GMOs. There are two solutions, often complementary: physical containment and biocontainment. The first consists of putting mechanical barriers between the GMO and the external environment: we are using a micro scale membrane to make sure the microorganisms could not escape from the system. The second barrier is the biocontainment aiming at modifying the GMO’s culture preferences to avoid its survival under hostile conditions outside the container ([Link to Safety for more](https://2023.igem.wiki/ionis-paris/safety)).    

## Energy Efficiency

### Energy needed 

The space heating energy consumption is about 70 kWh/m2 or 70 J/s/m2  [3]. Imagine we want to heat a 100 m2 appartement, the system would then need to produce 7000 kJ/s, so 2.52 x 107 kJ/h. 

### Energy produced 

Knowing that our system is able to produce 1.6x10^3 kJ/h/L ([Link to Model](https://2023.igem.wiki/ionis-paris/model)) which is amplified by a heat pump, we can use the Coefficient of Performance (COP) formula :

COP = Delivered Energy / Input Energy


Let set the COP of the heat pump is 4, it means the heat pump can move 4 times the amount of heat energy for every unit of input energy. Therefore, the output heat energy would be: 

Output Heat Energy = COP * Input Energy
Output Heat Energy = 4 x 1.6 x 10^3 = 6.4 x 10^3 kJ/h/L

So, with a COP of 4, the heat pump can amplify the energy produced up to 6.4 × 10^3 kJ/h/L. 

### To determine the volume of the photobioreactor for a 100 m2 appartement:  

V = (6.4 * 10^3 kJ/h/L) / (2.52 * 10^7 kJ/h) = 3 940 L

So the photobioreactor could be high of 2,5 m and large of 1,4 m. 

### Maintenance: 

The maintenance of our system must be conducted by qualified technicians and our system is designed to facilitate their work. We integrated to our model an access hatch with a ladder to provide a convenient entry for the technicians to be able to go monitor the pH, temperature, O2 and CO2 sensors at the window. 

By going further below with the ladder, a platform is provided to reach the drainage valve and the filtration system to collect the biomass. Therefore, technicians can retrieve biomass before it can escape to the wastewater treatment station with the wastewater. 

And finally, our photobioreactor is equipped with a cover that technicians can use to perform high-pressure cleaning on the tubes of the heat exchanger. This ensures that any microalgae buildup is effectively removed, maintaining the efficiency of the system. 

## Sustainability

### Sustainability and Cost Savings: 

- By utilizing wastewater as a heat source, homeowners reduce their reliance on traditional heating methods and contribute to sustainable practices. This transition leads to potential cost savings on energy bills over time.  

Most of our system's electricity consumption is concentrated on three elements: 

   - **LED Lighting:** Our photobioreactor incorporates four LED lights, each consuming 27 watts [4]. These LEDs provide efficient lighting within the system while consuming a total of 108 watts, which is equivalent to 946 kWh of annual electricity consumption. 

   - **Water-to-Water Heat Pump:** Our system utilizes a high-temperature water-to-water heat pump, which needs an input energy of 1.2 kW in 1h [5]. Which means that the annual electricity consumption of the heat pump is 10,436 kWh. It is the most consuming component of the system. 

   - **Water Pump:** The model includes a submersible water pump for the reservoir. While its electricity consumption is about 0.45 kW [6], which is equivalent to 6,488 kWh in a year, if the pump is active only 8 hours a day since our system is discontinuous. 

We have some additional components, such as a bubble stirrer [7] and various sensors, which have minimal electricity requirements. 

 According to Engie's data, the average household annual electricity consumption for heating in Paris is 2,024 kWh [8]. It means that if for example **our system enables to heat a building of 10 appartements, or 10 individual houses, it will consume less electrical energy for its functional than a single household need to for heating.** Moreover, the system could generate additional income through the revalorization of biomass and urine, as elaborated in the next section.

### Revalorization:  

- A significant aspect of our project is the revalorization of biomass and urine. The biomass produced in the photobioreactor and filtered can be processed into biofuel, a valuable and sustainable energy source. This revalorization not only reduces waste but also provides homeowners with an additional resource for energy or sale. In addition to biomass and to maximize the benefits, the used urine in the habitat can also be repurposed. The processed urine can be transformed into fertilizers, creating a valuable agricultural product that can be sold or used to enhance local soil quality. 

### Environmental Impact: 

Implementing Clean Heat in residential settings has a positive impact on the environment by reducing energy consumption and mitigating water pollution, all while providing comfortable and sustainable heating. We have conducted a carbon impact assessment to prove our point ([Link to Sustainable Development](https://2023.igem.wiki/ionis-paris/human-practices/sustainable-development)). 

### Pollutant Degradation: 

Our system targets the degradation of synthetic estrogens and other pollutants in wastewater. By implementing Clean Heat, homeowners actively contribute to cleaner water sources and reduced environmental pollution.

In summary, our project, Clean Heat, proposes a forward-thinking solution for homeowners seeking sustainable and eco-friendly heating options. By integrating this system into residential properties, we envision a future where households can both reduce their environmental footprint and enjoy the benefits of efficient, clean, and cost-effective heating. 

### References:  

[1] Maltsev, Yevhen, Kateryna Maltseva, Maxim Kulikovskiy, et Svetlana Maltseva. 2021. « Influence of Light Conditions on Microalgae Growth and Content of Lipids, Carotenoids, and Fatty Acid Composition ». Biology 10(10): 1060. 

[2] Flynn, K. J., Mitra, A., Greenwell, H. C., & Sui, J. (2013). Monster potential meets potential monster: pros and cons of deploying genetically modified microalgae for biofuels production. Interface Focus, 3(1), 20120037. https://doi.org/10.1098/rsfs.2012.0037). 

[3] Airaksinen, Miimu, et Mika Vuolle. 2013. « Heating Energy and Peak-Power Demand in a Standard and Low Energy Building ». Energies 6: 235‑50) 

[4] Horticultural LEDs from GOWLED 
https://www.growled.fr/greenvisualed-eclairage-led-plantes-mur-vegetal/330-barre-led-spectraline-greenvisualed-rendu-lumineux-blanc-37010482588.html 

[5] Datasheet of High-Temperature Heat Pump from nordicghp.com, WH-Series High temperature. 
https://www.nordicghp.com/wp-content/uploads/2023/05/002500MAN-01-ISSUE-05-W-WH-WP-Series.pdf 

[6] Datasheet of submersible pump from MPS. 
https://www.labonnepompe.com/fiche-technique?id_attachment=149&FT%20Calpeda%20MPS 

[7] Erell OLIVO (2007). Conception et étude d’un photobioréacteur pour la production en continu de microalgues en écloseries aquacoles. Thèse de doctorat, école polytechnique de l’Université de NANTES, (p.64-65/263). https://archimer.ifremer.fr/doc/2008/these-3847.pdf . 

[8] Engie: Individual electricity consumption in kWh/year for heating (2022) 
https://particuliers.engie.fr/electricite/conseils-electricite/conseils-tarifs-electricite/consommation-moyenne-electricite-personne.html#:~:text=Sommaire&text=En%20France%2C%20la%20consommation%20moyenne,par%20r%C3%A9sidence%20principale(2). 
        `
        }
      </PageContent>
    </div>
  );
}

export default Implementation;
